import React, { Component } from 'react'
import { Button } from 'reactstrap'
import { Field, reduxForm } from 'redux-form'
import { renderField } from '../../../utilities/reduxForm.util'
import FormattedMessage from '../../FormattedMessage/FormattedMessage'
import { SelectInput } from '../../Input'
import {
  required,
  validAge,
  emailValidator,
  dateRequired,
  password,
  maxLength,
  minLength,
} from '../../../utilities/validators'
import languageFileOptions from '../../../../shared/languageCodes.json'
import countryCodes from '../../../../shared/countryCodes.json'
import Loader from '../../Loader/Loader'

const validate = values => {
  const errors = {}
  if (values.email !== values.confirmEmail) {
    errors.confirmEmail = 'error.email.verifyFailed'
  }
  if (!values.dob) {
    errors.dob = 'global.label.requiredField'
  }
  return errors
}

const [minLength6, maxLength20] = [minLength(6), maxLength(20)]

class RegisterForm extends Component {
  constructor(props) {
    super(props)

    this.state = {
      languageOptions: [],
    }
    this.focusInput = React.createRef()
  }

  componentDidMount() {
    const languageOptions = languageFileOptions
      .filter(lang => lang.active)
      .map(lang => ({ value: lang.code, key: lang.nativeName }))
      .sort((a, b) => a.value.localeCompare(b.value))
    this.focusInput.current.focus()
    this.setState({ languageOptions })
  }

  render() {
    const {
      error,
      invalid,
      submitFailed,
      countryLoading,
      registerLoading,
      countryCode,
      languageCode,
      handleChange,
      handleSubmit,
      isModal = false,
    } = this.props
    const { languageOptions } = this.state

    const isCaptchaError = error === 'global.label.timeBlock'

    return (
      <div>
        <form onSubmit={handleSubmit} method="post" aria-labelledby="register-form-title">
          <div id="register-form-title" className="sr-only">
            Registration form
          </div>
          <div disabled={registerLoading}>
            <div className="sr-only">Account registration</div>
            <div className="row">
              <Field
                component={renderField}
                validate={[required]}
                inputRef={this.focusInput}
                inputType="input"
                type="text"
                containerclass="col-md-12 col-lg-6 form-group"
                name="firstname"
                label="global.label.firstName"
              />
              <Field
                component={renderField}
                validate={[required]}
                inputType="input"
                type="text"
                containerclass="col-md-12 col-lg-6 form-group"
                name="lastname"
                label="global.label.lastName"
              />
            </div>
            <div className="row">
              <Field
                component={renderField}
                validate={[dateRequired, validAge]}
                inputType="date"
                type="date"
                containerclass="col form-group"
                name="dob"
                label="global.label.birthdate"
              />
            </div>
            <div className="row">
              <div className="col-md-12 col-lg-6 form-group">
                <label className="form-label-sm">
                  <FormattedMessage id="global.label.country" />
                </label>
                {countryLoading ? (
                  <Loader aria-label="Loading country information" />
                ) : (
                  <div
                    className="form-control"
                    role="textbox"
                    aria-readonly="true"
                    aria-describedby="country-readonly-note"
                  >
                    {countryCodes.map(country => country.code === countryCode && country.name)}
                  </div>
                )}
                <div id="country-readonly-note" className="sr-only">
                  This field cannot be modified
                </div>
              </div>
              <div className="col-md-12 col-lg-6 form-group">
                {countryLoading ? (
                  <div>
                    <label className="form-label-sm">
                      <FormattedMessage id="global.label.language" />
                    </label>
                    <Loader aria-label="Loading language options" />
                  </div>
                ) : (
                  <SelectInput
                    className="form-control"
                    name="language"
                    label="global.label.language"
                    value={languageCode}
                    options={languageOptions}
                    onChange={handleChange}
                    aria-label="Select preferred language"
                  />
                )}
              </div>
            </div>
            <div className="row form-group">
              <Field
                component={renderField}
                validate={[required, emailValidator]}
                type="email"
                name="email"
                label="global.label.email"
                containerclass="col-md-12"
                inputType="input"
              />
            </div>
            <div className="row form-group">
              <Field
                component={renderField}
                validate={[required, emailValidator]}
                type="email"
                name="confirmEmail"
                label="global.label.emailConfirm"
                containerclass="col-md-12"
                inputType="input"
                aria-describedby="confirm-email-help"
              />
              <div id="confirm-email-help" className="sr-only">
                Re-enter your email address to confirm
              </div>
            </div>
            <div className="row form-group">
              <Field
                component={renderField}
                validate={[required, minLength6, maxLength20, password]}
                type="password"
                password
                name="password"
                label="global.label.password"
                containerclass="col-md-12"
                inputType="input"
                aria-describedby="password-requirements"
              />
              <div id="password-requirements" className="sr-only">
                Password must be 6-20 characters long
              </div>
            </div>
            <Field
              component={renderField}
              validate={[required]}
              containerclass={isModal ? 'form-check pt-4 modalCheckbox' : 'form-check pt-4'}
              inputType="checkbox"
              id="toc"
              name="toc"
              type="checkbox"
              label="page.registrationPage.global.registrationAccept"
              aria-required="true"
            />
            <Field
              component={renderField}
              containerclass={isModal ? 'form-check mb-3 modalCheckbox' : 'form-check mb-3'}
              inputType="checkbox"
              id="amdNews"
              name="amdNews"
              type="checkbox"
              label="page.registrationPage.global.registrationSubscribe"
              aria-required="false"
            />

            {invalid && submitFailed && (
              <div className="form-validation-error-message mb-2" role="alert" aria-live="polite">
                <FormattedMessage
                  id={`${isCaptchaError ? error : 'error.registration.requiredFields'}`}
                />
              </div>
            )}
            {registerLoading ? (
              <Loader aria-label="Creating account" />
            ) : (
              <Button
                name="registerFormSubmitBtn"
                type="submit"
                outline
                color={isModal ? 'light' : 'dark'}
                block
                aria-disabled={registerLoading}
                aria-describedby={invalid && submitFailed ? 'registration-form-error' : undefined}
              >
                <FormattedMessage id="global.label.register" />
              </Button>
            )}
          </div>
        </form>
      </div>
    )
  }
}

export default reduxForm({
  form: 'registerForm',
  validate,
})(RegisterForm)
